import React, { useEffect, useRef, useContext, useState, useMemo, useCallback } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PADSideNavbar from './components/PADSideNavbar';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/solid';
import { Tooltip } from 'react-tooltip';
import { QuestionMarkCircleIcon, CheckCircleIcon } from '@heroicons/react/solid';
import SelectionOptions from './components/SelectionOptions';
import ModuleCompletionDialog, { CancelButton, GenerateReportButton, SummarySection } from '../../../components/ModuleCompletionDialog';
import * as Dialog from '@radix-ui/react-dialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { auth } from '../../../firebase';
import { SearchIcon, ClockIcon, XIcon } from '@heroicons/react/solid';
import AutoResizingTextArea from '../../../components/AutoResizingTextArea';
import { formatDateTime } from '../../../utils/timestampUtils';
import { FormattedAnswer, normalizeRawAnswer, buildDisplayAnswer } from '../../../utils/formDisplayUtils';
import { serverTimestamp } from 'firebase/firestore';
import { buildFirstGeneratedFields, buildFirstCompletedFields, buildPadCompletionFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { RefreshIcon } from '@heroicons/react/outline';
import { isPadAdhdOutcomePositive } from '../../../utils/conditionChecks';
import useDebouncedCallback from '../../../hooks/useDebouncedCallback';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import {
  getPadStageOrder,
  getPadSidebarIndex,
  getPadSidebarSections,
  getPadNavLinks,
  rewriteSubsectionTitleDisplay,
  readIsCarryOutHealthChecksNow,
  buildPadSidebarUnlockFlags,
} from '../../../utils/padNavigationUtils';

export default function PADHealthChecks() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();

  // Whether this org's PAD includes the Agreed Medication Plan (AMP) stage.
  // For Well Life Clinic this is false: AMP is hidden from the sidebar, the
  // "Yes" option is removed from "Create medication plan now?", and the PAD
  // ends here at Health Checks.
  const { isAgreedMedicationPlanEnabled, isPadTreatmentFirst = false, isAppointmentDatesEnabled = false, isAdhdControlledDrugPrescribingEnabled = false } = useWorkflowConfig(patientDataModel, claims);

  const [hasChanges, setHasChanges] = useState(false);

  // Determines whether this user is the clinician assigned to the PAD stage.
  // When false the entire page renders in read-only mode.
  const canEdit = useCanEditStage(patientDataModel, 'PAD');

  // Registers dirty state with ProformaNavigationGuardContext and provides
  // unmount-based regeneration as a safety net for unmanaged exits (breadcrumb,
  // top nav, browser back). markRegenHandled() is called in handleSectionCompletion
  // to prevent double-firing when the managed Next/Finish/Sidebar path is used.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: 'Post-Assessment Discussion',
    sectionTitle: 'Health Checks',
    hasChanges,
    canEdit,
    patientDataModel,
  });

  const [formState, setFormState] = useState({});
  const [formValid, setFormValid] = useState(false);

  const [clinicianInputs, setClinicianInputs] = useState({});
  // Whether the clinician has typed in the focused reading since clicking
  // into it. A click in and straight back out is not an edit: the blur
  // handler must then write nothing, or it would stamp a "clinician input"
  // the clinician never made and recompute the BMI from an empty value
  // (which is how a mere click used to blank the BMI until refresh).
  const editedSinceFocusRef = useRef(false);
  // Track one-time initialization to avoid overwriting local edits with snapshots
  const isInitialInputsLoaded = useRef(false);
  // Track dirty fields and in-flight writes per field
  const dirtyFieldsRef = useRef(new Set());
  const inFlightRef = useRef({});

  // Debouncer for per-field saves
  const { schedule, flush } = useDebouncedCallback(500);

  const getPlaceholderForField = (title) => {
    const titleLower = title.toLowerCase();

    if (titleLower.includes('pulse'))
      return "Input pulse with suffix bpm";
    if (titleLower.includes('blood pressure'))
      return "Input as systolic/diastolic with suffix mmHg";
    if (titleLower.includes('height'))
      return "Input height with suffix cm";
    if (titleLower.includes('weight'))
      return "Input weight with suffix kg";
    if (titleLower.includes('bmi'))
      return "";

    return "No answer given";
  };

  const getBMICategory = (bmi) => {
    const bmiValue = parseFloat(bmi);
    if (isNaN(bmiValue)) return "";
    if (bmiValue < 18.5) return "Underweight";
    if (bmiValue >= 18.5 && bmiValue <= 24.9) return "Normal Weight";
    if (bmiValue >= 25 && bmiValue <= 29.9) return "Overweight";
    if (bmiValue >= 30 && bmiValue <= 34.9) return "Obese Class 1";
    if (bmiValue >= 35 && bmiValue <= 39.9) return "Obese Class 2";
    if (bmiValue >= 40) return "Obese Class 3 (Severe Obesity)";
    return "";
  };

  // Function to extract numeric value from input with units
  const extractNumericValue = (input) => {
    if (!input) return null;
    // Extract digits, decimal points, and minus signs
    const match = input.toString().match(/-?\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : null;
  };

  // Function to calculate BMI
  const calculateBMI = (height, weight) => {
    const heightValue = extractNumericValue(height);
    const weightValue = extractNumericValue(weight);

    if (!heightValue || !weightValue) return null;

    // Calculate BMI: weight (kg) / (height (m) ^ 2)
    // Height is assumed to be in cm and needs conversion to meters
    const heightInMeters = heightValue / 100;
    const bmi = weightValue / (heightInMeters * heightInMeters);

    // Round to 1 decimal place
    return Math.round(bmi * 10) / 10;
  };

  const handleClinicianInputChange = (questionKey, value) => {
    if (!canEdit) return;
    editedSinceFocusRef.current = true;
    setClinicianInputs(prev => ({
      ...prev,
      [questionKey]: value
    }));
    setHasChanges(true);
    // mark field dirty
    dirtyFieldsRef.current.add(questionKey);
    // schedule debounced save for this field only
    schedule(questionKey, async () => {
      try {
        // increment in-flight version
        inFlightRef.current[questionKey] = (inFlightRef.current[questionKey] || 0) + 1;
        const version = inFlightRef.current[questionKey];

        const trimmedValue = (value || '').toString().trim();
        const updateObj = {
          clinicianInput: trimmedValue === '' ? null : trimmedValue,
          clinicianInputUpdatedAt: serverTimestamp(),
          clinicianInputUpdatedBy: auth.currentUser?.uid || 'Unknown ID',
          clinicianInputUpdatedByName: auth.currentUser?.displayName || 'Unknown user'
        };

        await updatePatientFirestore(patientId, {
          '2_clinicalInterview': {
            'sections': {
              '8': {
                'subsections': {
                  '1': {
                    'subsectionQuestions': {
                      [questionKey]: updateObj
                    }
                  }
                }
              }
            }
          }
        });

        // if still latest, clear dirty
        if (inFlightRef.current[questionKey] === version) {
          dirtyFieldsRef.current.delete(questionKey);
        }
      } catch (e) {
        // keep dirty to retry later
        console.error('Debounced save failed for', questionKey, e);
      }
    });
  };

  // Enhanced blur handler that only updates BMI when height or weight actually changes
  // IMPORTANT: All health check updates go to dataModel (Clinical Interview section),
  // NOT to the patient's formData. This is intentional - even for patients who didn't
  // fill out physical health questions, we still need to store health check data.
  const handleClinicianInputBlur = async (questionKey, value) => {
    if (!canEdit) return;

    // Nothing typed since the click: leave the stored value, the BMI and
    // the audit stamps exactly as they were. See editedSinceFocusRef.
    if (!editedSinceFocusRef.current) return;
    editedSinceFocusRef.current = false;

    try {
      const questions = patientDataModel.dataModel['2_clinicalInterview']['sections']['8'].subsections['1'].subsectionQuestions;
      const trimmedValue = (value || '').toString().trim();

      // Cancel pending debounce and flush immediate save
      await flush(questionKey, async () => {
        const updates = {};

        // Always include the edited field write
        updates['2_clinicalInterview'] = {
          'sections': {
            '8': {
              'subsections': {
                '1': {
                  'subsectionQuestions': {
                    [questionKey]: {
                      clinicianInput: trimmedValue === '' ? null : trimmedValue,
                      clinicianInputUpdatedAt: serverTimestamp(),
                      clinicianInputUpdatedBy: auth.currentUser?.uid || 'Unknown ID',
                      clinicianInputUpdatedByName: auth.currentUser?.displayName || 'Unknown user'
                    }
                  }
                }
              }
            }
          }
        };

        // If height/weight changed, compute BMI and include in same write
        const lowerTitle = questions[questionKey].title.toLowerCase();
        const isBmiRelated = lowerTitle.includes('height') || lowerTitle.includes('weight');
        if (isBmiRelated) {
          // derive keys
          let heightKey, weightKey, bmiKey;
          Object.entries(questions).forEach(([key, q]) => {
            const t = q.title.toLowerCase();
            if (t.includes('height')) heightKey = key;
            if (t.includes('weight') && !t.includes('height')) weightKey = key;
            if (t.includes('bmi')) bmiKey = key;
          });
          if (heightKey && weightKey && bmiKey) {
            const currentHeight = (questionKey === heightKey ? trimmedValue : (clinicianInputs[heightKey] || questions[heightKey].clinicianInput || questions[heightKey].patientPrefill || ''));
            const currentWeight = (questionKey === weightKey ? trimmedValue : (clinicianInputs[weightKey] || questions[weightKey].clinicianInput || questions[weightKey].patientPrefill || ''));

            // compute BMI using shared helpers
            const bmiValue = calculateBMI(currentHeight, currentWeight);
            let bmiFormatted = null;
            if (bmiValue !== null) {
              const bmiCategory = getBMICategory(bmiValue);
              bmiFormatted = `${bmiValue} (${bmiCategory})`;
            }

            updates['2_clinicalInterview']['sections']['8']['subsections']['1']['subsectionQuestions'][bmiKey] = {
              clinicianInput: bmiFormatted,
              clinicianInputUpdatedAt: serverTimestamp(),
              clinicianInputUpdatedBy: auth.currentUser?.uid || 'Unknown ID',
              clinicianInputUpdatedByName: auth.currentUser?.displayName || 'Unknown user'
            };

            // Mirror the write in local state. When there is no BMI to show
            // the key is REMOVED rather than set to "": the field falls back
            // to the stored value and then to the patient's prefilled BMI,
            // the same way Reset does. An empty string would be shown in
            // preference to both and read as "no BMI".
            setClinicianInputs(prev => {
              const next = { ...prev };
              if (bmiFormatted) next[bmiKey] = bmiFormatted;
              else delete next[bmiKey];
              return next;
            });
          }
        }

        await updatePatientFirestore(patientId, updates);
        dirtyFieldsRef.current.delete(questionKey);
        setHasChanges(true);

        // A cleared reading is stored as clinicianInput null, meaning "not
        // overridden by the clinician". Drop the local "" too so the field
        // shows the patient's reading again (and the Reset button goes),
        // which is exactly what a page refresh would show.
        if (trimmedValue === '') {
          setClinicianInputs(prev => {
            const next = { ...prev };
            delete next[questionKey];
            return next;
          });
        }
      });
    } catch (error) {
      console.error("Error updating clinician input:", error);
    }
  };

  // (Removed unused handleBMIRecalculation after introducing atomic BMI write in blur handler)

  // Modify the reset handler function to also reset BMI when height/weight is reset
  const handleResetClinicianInput = async (questionKey) => {
    if (!canEdit) return;
    try {
      // Get the questions object
      const questions = patientDataModel.dataModel['2_clinicalInterview']['sections']['8'].subsections['1'].subsectionQuestions;

      // Check if we're resetting height or weight
      const lowerTitle = questions[questionKey].title.toLowerCase();
      const isBmiRelated = lowerTitle.includes('height') || lowerTitle.includes('weight');

      // Prepare the reset update
      let updateObj = {
        '2_clinicalInterview': {
          'sections': {
            '8': {
              'subsections': {
                '1': {
                  'subsectionQuestions': {
                    [questionKey]: {
                      clinicianInput: null,
                      clinicianInputUpdatedAt: null,
                      clinicianInputUpdatedBy: null,
                      clinicianInputUpdatedByName: null
                    }
                  }
                }
              }
            }
          }
        }
      };

      // If resetting height or weight, also reset BMI
      if (isBmiRelated) {
        let bmiKey;

        // Find the BMI question key
        Object.entries(questions).forEach(([key, question]) => {
          const title = question.title.toLowerCase();
          if (title.includes('bmi')) bmiKey = key;
        });

        if (bmiKey) {
          // Add BMI reset to update object
          updateObj['2_clinicalInterview']['sections']['8']['subsections']['1']['subsectionQuestions'][bmiKey] = {
            clinicianInput: null,
            clinicianInputUpdatedAt: null,
            clinicianInputUpdatedBy: null,
            clinicianInputUpdatedByName: null
          };

          // Delete BMI key from local state rather than setting to null
          setClinicianInputs(prev => {
            const newState = { ...prev };
            delete newState[bmiKey]; // Delete the key entirely instead of setting to null
            return newState;
          });
        }
      }

      // Update Firestore
      await updatePatientFirestore(patientId, updateObj);

      // Update local state for the reset field
      // Delete the key from local state entirely instead of setting to null
      setClinicianInputs(prev => {
        const newState = { ...prev };
        delete newState[questionKey];
        return newState;
      });

      setHasChanges(true);
    } catch (error) {
      console.error("Error resetting clinician input:", error);
    }
  };

  // Initialize clinician inputs from Firestore data
  useEffect(() => {
    if (isInitialInputsLoaded.current) return;
    if (patientDataModel?.dataModel?.['2_clinicalInterview']?.['sections']?.['8']?.subsections?.['1']?.subsectionQuestions) {
      const questions = patientDataModel.dataModel['2_clinicalInterview']['sections']['8'].subsections['1'].subsectionQuestions;

      const initialClinicianInputs = {};
      Object.entries(questions).forEach(([key, question]) => {
        if (question.clinicianInput !== undefined && question.clinicianInput !== null) {
          initialClinicianInputs[key] = question.clinicianInput;
        }
      });

      setClinicianInputs(initialClinicianInputs);
      isInitialInputsLoaded.current = true;
    }
  }, [patientDataModel]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Handles the "Finish Post-Assessment Discussion" button click.
   * 
   * Saves all section data and health check values, then shows a confirmation dialog.
   * Uses try/finally to ensure the saving state is always reset, preventing the button
   * from getting stuck in "Saving..." state if an error occurs during the save process.
   */
  const handleFinish = async () => {
    setIsSaving(true);
    try {
      await handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
      setIsDialogOpen(true);
    } catch (error) {
      // Log the error for debugging but ensure we recover gracefully
      console.error("Error completing section:", error);
      // Optionally could show an error toast/alert here to inform the clinician
    } finally {
      // Always reset the saving state to prevent the button from being stuck
      setIsSaving(false);
    }
  };

  const handleGenerateReport = () => {
    setIsDialogOpen(false);
    setIsGenerating(true);
  };

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };

  const handleNextPageButtonClick = () => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['5_postAssessmentDiscussion']);

    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': buildPadCompletionFields(patientDataModel, firstCompletedFields),
      '6_manageReports': {
        padReportGenerated: true,
        padReportGeneratedAt: serverTimestamp(),
        padReportGeneratedBy: auth.currentUser.uid,
        padReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('pad', manageReports),
      }
    });

    appendReportAuditEntry(patientId, {
      module: 'pad',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.padReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'pad',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: isAppointmentDatesEnabled,
    });

    navigate(`/patient/${patientId}/documents`);
  };

  const [openDialogIndex, setOpenDialogIndex] = useState(null);
  const [rawPrefillData, setRawPrefillData] = useState([]);
  const [dialogPrefillText, setDialogPrefillText] = useState('');

  // Update this function to fetch raw prefill data when dialog opens
  const handleOpenRawPrefillDialog = (index) => {
    if (patientDataModel?.dataModel?.['2_clinicalInterview']?.['sections']?.['8']?.subsections?.['1']?.subsectionQuestions) {
      const clinicalInterviewData = patientDataModel.dataModel['2_clinicalInterview']['sections']['8'].subsections['1'].subsectionQuestions;
      const questions = Object.values(clinicalInterviewData);

      if (questions[index]) {
        const question = questions[index];
        const varNames = question.prefilledByServiceUserVarNames || [];

        if (varNames.length > 0 && !question.clinicianInput) {
          const rawPrefillData = getRawPrefillInputs(varNames);
          setRawPrefillData(rawPrefillData);
          setDialogPrefillText(question.patientPrefill || '');
          setOpenDialogIndex(index);
        } else {
          // Simple fallback or for clinician input
          setRawPrefillData([{
            formName: question.clinicianInput ? "Clinician Input" : "Your Health",
            question: question.title,
            answer: question.clinicianInput || question.patientPrefill || "No previous answer given",
            hasAnswer: !!(question.clinicianInput || question.patientPrefill),
            lastUpdated: question.clinicianInputUpdatedAt || question.patientPrefillLastUpdatedAt,
            updatedBy: question.clinicianInput ? (question.clinicianInputUpdatedByName || "Unknown clinician") : null
          }]);
          setDialogPrefillText(question.patientPrefill || '');
          setOpenDialogIndex(index);
        }
      }
    }
  };

  // Extracts raw prefill inputs from the patient's "Your Physical Health"
  // subsection by scanning formSubsections for the matching title. This
  // handles both legacy patients (Physical Health at key '1') and new
  // patients (key '7') without hardcoding any particular subsection key.
  // For patients with workflowIncludesPhysicalHealthQuestions = false the
  // subsection won't exist in their formData; the function returns an
  // empty array so clinicians can enter data fresh during the PAD.
  const getRawPrefillInputs = (varNames) => {
    const subsections = patientDataModel?.formData?.yourHealth?.formSubsections;
    const physicalHealthSub = subsections
      ? Object.values(subsections).find(
          (sub) => sub?.formSubsectionTitle === "Your Physical Health"
        )
      : null;

    if (!physicalHealthSub?.questions || !varNames || varNames.length === 0) {
      return [];
    }

    const results = [];
    const yourHealthQuestions = physicalHealthSub.questions;

    // Safety check: if we somehow got past the initial check but questions is undefined, return empty array
    if (!yourHealthQuestions) {
      return [];
    }

    // Go through all questions in "Your Health" form
    Object.entries(yourHealthQuestions).forEach(([questionKey, question]) => {
      // Check each subquestion
      if (question.subquestions) {
        Object.entries(question.subquestions).forEach(([subquestionKey, subquestion]) => {
          // Skip non-numbered subquestion keys
          if (isNaN(parseInt(subquestionKey))) return;

          // If this subquestion's variable name is in our list
          if (varNames.includes(subquestion.serviceUserVarName)) {
            const normalized = normalizeRawAnswer(subquestion.answer, subquestion.otherAnswer);
            const allOptions = [...(subquestion.options || []), ...(subquestion.noneOptions || [])];

            results.push({
              formName: "Your Health",
              questionTitle: question.questionTitle,
              question: subquestion.subquestionDisplayText,
              answer: buildDisplayAnswer(normalized, 'No previous answer given'),
              hasAnswer: normalized !== null && normalized !== undefined && normalized !== '',
              inputType: subquestion.inputType || '',
              options: allOptions,
              rawAnswer: normalized,
              wasSkipped: question.wasSkipped,
              skipButtonText: question.skipButtonText
            });
          }
        });
      }
    });

    return results;
  };

  // Constants
  const currentStageTitle = 'Post-Assessment Discussion';
  const currentSectionNumber = 4;
  const currentSectionTitle = 'Health Checks';
  const nextSectionLink = '/proforma/post-assessment-discussion/agreed-medication-plan';
  const nextSectionTitle = 'Agreed Medication Plan';

  // Ordered stage list for the clinician PAD — controls sidebar order, display
  // numbers, and prev/next links. Treatment-first (SLaM) swaps sections 2/3.
  const padStageOrder = useMemo(
    () => getPadStageOrder({ isPadTreatmentFirst, isAgreedMedicationPlanEnabled }),
    [isPadTreatmentFirst, isAgreedMedicationPlanEnabled]
  );

  // Navigation links derived from the ordered stage list. For Health Checks
  // the back link adapts: Treatment Options (strategies-first) or Strategies
  // (treatment-first). isCarryOutHealthChecksNow doesn't affect HLT's own
  // links since HLT is not the gate-owner page.
  const padNav = useMemo(
    () => getPadNavLinks('4', padStageOrder, { isCarryOutHealthChecksNow: true }),
    [padStageOrder]
  );
  const prevSectionLink = padNav.prevLink || '/proforma/post-assessment-discussion/treatment-options';
  const prevSectionTitle = padNav.prevTitle || 'Treatment Options';

  // Get the section data using useMemo
  const sectionData = useMemo(() => {
    return patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber] || {};
  }, [patientDataModel]);

  // Get subsections data
  const subsections = useMemo(() => {
    return sectionData?.subsections || {};
  }, [sectionData]);

  // Ordered subsection entries for rendering. Filters out the 'prescribing'
  // panel when the workflow flag is off, and sorts so that 'prescribing'
  // appears between Cardiovascular ('3') and Outcome ('4') when visible.
  const visibleSubsections = useMemo(() => {
    if (!subsections) return [];
    return Object.entries(subsections)
      .filter(([key]) => {
        if (key === 'prescribing') return isAdhdControlledDrugPrescribingEnabled;
        return true;
      })
      .sort(([a], [b]) => {
        const order = (k) => k === 'prescribing' ? 3.5 : (Number(k) || 99);
        return order(a) - order(b);
      });
  }, [subsections, isAdhdControlledDrugPrescribingEnabled]);

  // Add a ref to track initial load
  const isInitialLoad = useRef(true);

  // Update the checkConditionsToShow function to accept an optional state parameter
  const checkConditionsToShow = useCallback((conditions, stateToCheck = null) => {
    if (!conditions) return true;

    // Use provided state or default to formState
    const state = stateToCheck || formState;

    return Object.values(conditions).every(condition => {
      const fieldValue = state[condition.firestoreFieldName]?.value || [];

      // Check if value matches any required values
      if (condition.valueIsOneOf) {
        const hasMatchingValue = fieldValue.some(value =>
          condition.valueIsOneOf.includes(value)
        );
        if (!hasMatchingValue) return false;
      }

      // Check if value doesn't match any excluded values
      if (condition.valueIsNotOneOf) {
        const hasExcludedValue = fieldValue.some(value =>
          condition.valueIsNotOneOf.includes(value)
        );
        if (hasExcludedValue) return false;
      }

      return true;
    });
  }, [formState]);

  // Common function to update Firestore
  const updateFirestore = useCallback((newUpdates) => {
    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
    setHasChanges(true);
  }, [patientId, updatePatientFirestore, currentSectionNumber]);

  // Initialize form state from Firestore data
  useEffect(() => {
    // Only run on initial load
    if (!isInitialLoad.current) return;
    if (!sectionData?.subsections) return;

    const initialFormState = {};

    Object.entries(sectionData.subsections).forEach(([subsectionId, subsection]) => {
      // Initialize form state for questions
      if (subsection?.questions) {
        Object.entries(subsection.questions).forEach(([questionId, question]) => {
          if (question?.firestoreFieldName) {
            initialFormState[question.firestoreFieldName] = {
              value: question?.answer?.value || [],
              otherValue: question?.answer?.otherValue || ''
            };
          }
        });
      }
    });

    setFormState(initialFormState);

    // Mark initial load as complete
    isInitialLoad.current = false;
  }, [sectionData]);

  // Function to handle vertical selection changes
  const handleSelect = async (fieldName, selectedOption, isSelected, question, isNoneOption = false) => {
    if (!canEdit) return;
    try {
      // Get current selections
      const currentSelections = formState[fieldName]?.value || [];
      let newSelections;

      if (question.questionType === 'selectOne') {
        // For selectOne, only allow one option at a time
        newSelections = isSelected ? [] : [selectedOption];
      } else {
        // For selectMultiple
        if (isNoneOption) {
          // When selecting a none option, clear all other selections
          newSelections = isSelected ? [] : [selectedOption];
        } else {
          if (isSelected) {
            // Remove the option if it's already selected
            newSelections = currentSelections.filter(option => option !== selectedOption);
          } else {
            // Add the new option and remove any none options
            newSelections = [...currentSelections.filter(opt =>
              !question.noneOptions?.includes(opt)
            ), selectedOption];
          }
        }
      }

      // Check if this field is a parent field that affects other fields' visibility
      const dependentFields = [];

      // Identify any dependent fields by checking all questions in all subsections
      Object.entries(subsections).forEach(([subsectionId, subsection]) => {
        Object.entries(subsection?.questions || {}).forEach(([questionId, q]) => {
          // Check if this question has conditions that depend on the field we're changing
          if (q.conditionsToShow && Object.values(q.conditionsToShow).some(
            condition => condition.firestoreFieldName === fieldName
          )) {
            dependentFields.push({
              fieldName: q.firestoreFieldName,
              subsectionId,
              questionId
            });
          }
        });
      });

      // Create update object
      const updateObject = {
        value: newSelections,
        otherValue: formState[fieldName]?.otherValue || ''
      };

      // Update local state first
      setFormState(prev => ({
        ...prev,
        [fieldName]: updateObject
      }));

      // Find the subsectionId and questionId for this field
      let targetSubsectionId, targetQuestionId;

      // Loop through subsections to find the question with matching firestoreFieldName
      Object.entries(subsections).forEach(([subsectionId, subsection]) => {
        Object.entries(subsection?.questions || {}).forEach(([questionId, q]) => {
          if (q.firestoreFieldName === fieldName) {
            targetSubsectionId = subsectionId;
            targetQuestionId = questionId;
          }
        });
      });

      if (!targetSubsectionId || !targetQuestionId) {
        throw new Error(`Question not found for field: ${fieldName}`);
      }

      // Build a single nested-object write that covers the primary answer
      // and any dependent field resets. Using nested format (not dot-notation)
      // avoids conflicting ancestor/descendant field paths when this write
      // is batched with the health check write in handleSectionCompletion.
      const subsectionsPayload = {
        [targetSubsectionId]: {
          questions: {
            [targetQuestionId]: { answer: updateObject }
          }
        }
      };

      // Process dependent fields that may need to be reset
      const updatedFormState = { ...formState, [fieldName]: updateObject };
      for (const depField of dependentFields) {
        const depQuestion = subsections[depField.subsectionId]?.questions?.[depField.questionId];

        const shouldShowDep = checkConditionsToShow(depQuestion.conditionsToShow, updatedFormState);

        if (!shouldShowDep) {
          const resetValue = {
            value: [],
            otherValue: ''
          };

          // Add the dependent field reset into the same nested write
          if (!subsectionsPayload[depField.subsectionId]) {
            subsectionsPayload[depField.subsectionId] = { questions: {} };
          }
          subsectionsPayload[depField.subsectionId].questions[depField.questionId] = { answer: resetValue };

          setFormState(prev => ({
            ...prev,
            [depField.fieldName]: resetValue
          }));
        }
      }

      await updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [currentSectionNumber]: {
              subsections: subsectionsPayload
            }
          }
        }
      });
      setHasChanges(true);
    } catch (error) {
      console.error('Error updating Firestore:', error);
      // Revert local state if Firestore update fails
      setFormState(prev => ({
        ...prev,
        [fieldName]: {
          value: formState[fieldName]?.value || [],
          otherValue: formState[fieldName]?.otherValue || ''
        }
      }));
    }
  };

  // Function to handle changes in the "Other" text field
  const handleOtherTextChange = (fieldName, text) => {
    if (!canEdit) return;
    const updateObject = {
      value: formState[fieldName]?.value || [],
      otherValue: text || ''
    };

    setFormState(prevState => ({
      ...prevState,
      [fieldName]: updateObject
    }));
    setHasChanges(true);
  };

  // Function to handle blur event on the "Other" text field.
  // Uses nested-object format (not dot-notation) so the write can be
  // deep-merged with the health check write in handleSectionCompletion
  // when both land in the same queue batch. Dot-notation would create
  // conflicting ancestor/descendant field paths in Firestore's update().
  const handleOtherTextBlur = (fieldName) => {
    if (!canEdit) return;
    const currentValue = formState[fieldName];
    const updateObject = {
      value: currentValue?.value || [],
      otherValue: currentValue?.otherValue || ''
    };

    let targetSubsectionId, targetQuestionId;

    Object.entries(subsections).forEach(([subsectionId, subsection]) => {
      Object.entries(subsection?.questions || {}).forEach(([questionId, q]) => {
        if (q.firestoreFieldName === fieldName) {
          targetSubsectionId = subsectionId;
          targetQuestionId = questionId;
        }
      });
    });

    if (!targetSubsectionId || !targetQuestionId) {
      console.error(`Question not found for field: ${fieldName}`);
      return;
    }

    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [currentSectionNumber]: {
            subsections: {
              [targetSubsectionId]: {
                questions: {
                  [targetQuestionId]: {
                    answer: updateObject
                  }
                }
              }
            }
          }
        }
      }
    });
    setHasChanges(true);
  };

  // Optimistic computation
  const isCarryOutHealthChecksNow = useMemo(() => {
    return patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.['3']?.subsections?.['3']?.questions?.['2']?.answer?.value?.includes('Yes') || false;
  }, [patientDataModel]);

  const isBeginningMedicationNow = useMemo(() => {
    // When the AMP stage is disabled for this org (e.g. Well Life Clinic), the
    // medication-plan branch is never taken — even for a patient who had
    // previously stored "Yes" before the stage was turned off.  This keeps the
    // "Finish Post-Assessment Discussion" button showing and AMP locked/hidden.
    if (!isAgreedMedicationPlanEnabled) return false;

    // Find the "Create medication plan now?" question (section 4 → subsection 4 → question 2)
    const medPlanQuestion = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.['4']?.subsections?.['4']?.questions?.['2'];
    const fieldName = medPlanQuestion?.firestoreFieldName;
    // Prefer local form state first for instant UI
    const localSelectedYes = fieldName ? (formState[fieldName]?.value || []).includes('Yes') : undefined;
    const medicationPlanNow = typeof localSelectedYes === 'boolean'
      ? localSelectedYes
      : (medPlanQuestion?.answer?.value?.includes('Yes') || false);
    return isCarryOutHealthChecksNow && medicationPlanNow;
  }, [patientDataModel, formState, isCarryOutHealthChecksNow, isAgreedMedicationPlanEnabled]);

  // When the AMP stage is disabled (Well Life Clinic), the "Create medication
  // plan now?" question must not offer "Yes" — only "No - defer" and
  // "No - refer on to titration" remain.  We return a shallow-cloned question
  // with the option filtered out for display only; the stored Firestore value
  // is never mutated.  All other questions pass through unchanged.
  const getDisplayQuestion = useCallback((question) => {
    if (isAgreedMedicationPlanEnabled) return question;
    if (question?.firestoreFieldName !== 'isBeginningMedicationNow') return question;

    const { Yes: _removedOtherOption, ...remainingOtherOptions } = question.otherOptions || {};
    return {
      ...question,
      options: (question.options || []).filter((option) => option !== 'Yes'),
      otherOptions: remainingOtherOptions,
    };
  }, [isAgreedMedicationPlanEnabled]);

  const SECTIONS_PAD = useMemo(() => {
    const isDiagnosisADHD = isPadAdhdOutcomePositive(patientDataModel);

    return getPadSidebarSections(
      patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections,
      padStageOrder,
      buildPadSidebarUnlockFlags({
        patientDataModel,
        isPadTreatmentFirst,
        isDiagnosisADHD,
        isCarryOutHealthChecksNow,
        isBeginningMedicationNow,
      }),
    );
  }, [patientDataModel, isCarryOutHealthChecksNow, isBeginningMedicationNow, padStageOrder, isPadTreatmentFirst]);


  // Handle section completion — applies conditional guards to avoid redundant
  // writes on revisits. Health check values are written alongside the status
  // update on first completion (or when the clinician has made changes).
  const handleSectionCompletion = useCallback(() => {
    if (!canEdit) return Promise.resolve();

    markRegenHandled();

    const currentSectionData = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';
    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['4']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(currentSectionTitle) || false;

    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasChanges || !isCurrentSectionGenerated;

    if (!needsStatusWrite && !needsGeneration) return Promise.resolve();

    // Queue health check values alongside the status update when any work is
    // being done — these values may have been edited by the clinician and must
    // be committed before generation reads from the data model.
    const ciQuestions = patientDataModel?.dataModel?.['2_clinicalInterview']?.['sections']?.['8']?.subsections?.['1']?.subsectionQuestions;
    if (ciQuestions) {
      const updates = {};
      Object.entries(ciQuestions).forEach(([key, question]) => {
        const title = question.title.toLowerCase();
        let value, source;

        if (clinicianInputs[key] !== undefined && clinicianInputs[key] !== null) {
          value = clinicianInputs[key];
          source = "Clinician Input";
        } else if (question.clinicianInput) {
          value = question.clinicianInput;
          source = "Clinician Input";
        } else if (question.patientPrefill) {
          value = question.patientPrefill;
          source = "Patient Self-Report";
        } else {
          value = '';
        }

        if (title.includes('height')) {
          updates.pad_height = value;
          if (source) updates.pad_height_source = source;
        } else if (title.includes('weight') && !title.includes('height')) {
          updates.pad_weight = value;
          if (source) updates.pad_weight_source = source;
        } else if (title.includes('bmi')) {
          updates.pad_bmi = value;
        } else if (title.includes('pulse')) {
          updates.pad_pulse = value;
          if (source) updates.pad_pulse_source = source;
        } else if (title.includes('blood pressure')) {
          updates.pad_bloodPressure = value;
          if (source) updates.pad_bloodPressure_source = source;
        }
      });

      updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': { sections: { '4': updates } }
      });
    }

    if (needsStatusWrite) {
      updateFirestore({ status: 'completed', progress: 100 });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      TriggerGenerateDocumentSection(patientId, currentStageTitle, currentSectionTitle, [], dataReady);
    }

    return dataReady;
  }, [canEdit, patientDataModel, currentSectionNumber, updateFirestore, patientId, currentStageTitle, currentSectionTitle, hasChanges, clinicianInputs, updatePatientFirestore, flushPendingUpdates, markRegenHandled]);

  const [isNavigating, setIsNavigating] = useState(false);

  const handleNextSection = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      navigate(`/patient/${patientId}${nextSectionLink}`);
    });
  }, [isNavigating, patientId, navigate, nextSectionLink, handleSectionCompletion]);

  const navigateToSection = useCallback((index) => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      const section = SECTIONS_PAD[index];
      if (section.route) {
        navigate(`/patient/${patientId}${section.route}`);
      }
    });
  }, [isNavigating, patientId, navigate, SECTIONS_PAD, handleSectionCompletion]);

  const handleBackNavigation = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      navigate(`/patient/${patientId}${prevSectionLink}`);
    });
  }, [isNavigating, patientId, navigate, prevSectionLink, handleSectionCompletion]);

  // Validate all visible required questions across Outcome and (when
  // enabled) the Prescribing panel. Each visible question must have at
  // least one selected value.
  const validateForm = useCallback(() => {
    if (!sectionData?.subsections) return false;

    // Outcome of Health Checks (subsection '4') -- always validated
    const outcomeSubsection = sectionData.subsections['4'];
    if (!outcomeSubsection || !outcomeSubsection.questions) return false;

    let isValid = true;

    Object.entries(outcomeSubsection.questions).forEach(([questionId, question]) => {
      const shouldShow = checkConditionsToShow(question.conditionsToShow);
      if (shouldShow) {
        const fieldValue = formState[question.firestoreFieldName]?.value || [];
        if (fieldValue.length === 0) {
          isValid = false;
        }
      }
    });

    // ADHD / Controlled Drug Prescribing panel -- validated only when the
    // workflow flag is on and the subsection exists in the patient data.
    if (isAdhdControlledDrugPrescribingEnabled && sectionData.subsections['prescribing']) {
      const prescribingSub = sectionData.subsections['prescribing'];
      Object.entries(prescribingSub.questions || {}).forEach(([questionId, question]) => {
        const shouldShow = checkConditionsToShow(question.conditionsToShow);
        if (shouldShow) {
          const fieldValue = formState[question.firestoreFieldName]?.value || [];
          if (fieldValue.length === 0) {
            isValid = false;
          }
        }
      });
    }

    return isValid;
  }, [sectionData, formState, checkConditionsToShow, isAdhdControlledDrugPrescribingEnabled]);

  // Use useEffect to validate the form whenever formState changes
  useEffect(() => {
    setFormValid(validateForm());
  }, [formState, validateForm]);

  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}`],
    [currentStageTitle],
    [currentSectionTitle]
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex">
        <PADSideNavbar
          sections={SECTIONS_PAD}
          currentSectionIndex={getPadSidebarIndex(padStageOrder, '4')}
          navigateToSection={navigateToSection}
          isNavigating={isSaving || isNavigating}
        />
        <div className="flex-grow p-8">

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={handleBackNavigation}
              disabled={isSaving || isNavigating}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 ${(isSaving || isNavigating) ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              aria-label="Back to Previous Section"
            >
              {(isSaving || isNavigating)
                ? <ButtonSpinner className="w-5 h-5 mr-2" />
                : <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />}
              <span className="font-bold">{(isSaving || isNavigating) ? 'Loading...' : `Back to ${prevSectionTitle}`}</span>
            </button>

            {/* Conditionally render either Next Section button or Finish button */}
            {isBeginningMedicationNow ? (
              <button
                onClick={handleNextSection}
                disabled={(canEdit && !formValid) || isNavigating}
                className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto ${(canEdit && !formValid) || isNavigating
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'hover:bg-violet-800 bg-violet-900 text-white'
                  }`}
              >
                <span className="font-bold">{isNavigating ? 'Loading...' : `Go to ${nextSectionTitle}`}</span>
                {isNavigating
                  ? <ButtonSpinner className="w-5 h-5 ml-2" />
                  : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
              </button>
            ) : (
              canEdit && (
                <button
                  onClick={handleFinish}
                  disabled={!formValid || isSaving || isNavigating}
                  className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto ${!formValid
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : (isSaving || isNavigating)
                        ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white'
                        : 'hover:bg-violet-800 bg-violet-900 text-white'
                    }`}
                >
                  <span className="font-bold">{isSaving ? "Saving Progress..." : "Finish Post-Assessment Discussion"}</span>
                  {isSaving ? <ButtonSpinner className="w-5 h-5 ml-2" /> : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
              )
            )}
          </div>


          {/* Section Title */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{padNav.displayNumber}. {currentSectionTitle}</h2>
          </div>

          {/* Subsections */}
          {visibleSubsections.map(([subsectionId, subsection], visibleIndex) => {
            if (!subsection) return null;

            // Dynamically compute display title from position in the visible
            // list, ensuring correct numbering whether the prescribing panel
            // is shown or not (e.g. non-Phoenix: Outcome = 4.4; Phoenix: 4.5).
            const computedTitle = `${padNav.displayNumber}.${visibleIndex + 1} ${subsection.subsectionTitle}`;

            // Special handling for Physical Health Review (first subsection)
            if (subsectionId === '1' && subsection.subsectionTitle === "Physical Health Review") {
              return (
                <div key={subsectionId} className="p-8 mb-8 bg-gray-50 rounded-2xl border border-gray-200">
                  <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                    {computedTitle}
                  </h3>

                  {/* Patient Prefill Data
                      Note: For patients with workflowIncludesPhysicalHealthQuestions = false,
                      there will be no patient prefill data (question.patientPrefill will be null/empty).
                      The fields will display placeholders, allowing clinicians to enter data fresh.
                  */}
                  <div className="mb-6 mt-2 rounded-lg">
                    {patientDataModel?.dataModel?.['2_clinicalInterview']?.['sections']?.['8']?.subsections?.['1']?.subsectionQuestions &&
                      Object.entries(patientDataModel.dataModel['2_clinicalInterview']['sections']['8'].subsections['1'].subsectionQuestions).map(([key, question], index) => {
                        // Check if this is the BMI field
                        const isBmiField = question.title.toLowerCase().includes('bmi');

                        return (
                          <div key={key} className="text-slate-700 text-base flex text-left items-center mb-2">
                            <div className="w-96 flex items-center gap-2">
                              <span className="font-bold">{question.title}</span>

                              {isBmiField ? (
                                // For BMI field, only show "Auto-calculated" label
                                <p className="text-sm italic text-gray-600">(Auto-calculated)</p>
                              ) : (
                                // For non-BMI fields, show the data source
                                question.clinicianInput ? (
                                  <p className="text-sm italic text-gray-600">(from Clinician Input)</p>
                                ) : question.patientPrefill ? (
                                  <p className="text-sm italic text-gray-600">(from Patient Self-Report)</p>
                                ) : (
                                  <p className="text-sm italic text-gray-400">(No previous answer given by Patient)</p>
                                )
                              )}
                            </div>

                            <div className="flex items-center">
                              <AutoResizingTextArea
                                placeholder={getPlaceholderForField(question.title)}
                                value={
                                  // What the clinician has typed in this session wins
                                  // (including "" while they are clearing the field);
                                  // otherwise the stored clinician value, then the
                                  // patient's reading. The same rule applies whether or
                                  // not the field has focus: clicking into a reading
                                  // must not make its value vanish.
                                  clinicianInputs[key] ?? (question.clinicianInput || question.patientPrefill || "")
                                }
                                onChange={(e) => !isBmiField && canEdit && handleClinicianInputChange(key, e.target.value)}
                                onBlur={(e) => !isBmiField && canEdit && handleClinicianInputBlur(key, e.target.value)}
                                onFocus={() => {
                                  if (!isBmiField && canEdit) editedSinceFocusRef.current = false;
                                }}
                                className={`w-96 px-3 py-2 border rounded-md ${isBmiField || !canEdit
                                  ? 'bg-gray-100 text-gray-700 cursor-not-allowed'
                                  : 'bg-white text-gray-700 border-gray-300'
                                  }`}
                                disabled={isBmiField || !canEdit}
                                readOnly={isBmiField || !canEdit}
                              />


                              {/* Source button */}
                              {!isBmiField && (
                                <Dialog.Root open={openDialogIndex === index} onOpenChange={(open) => !open && setOpenDialogIndex(null)}>
                                  <Dialog.Trigger asChild>
                                    <div
                                      className="cursor-pointer ml-1 mt-2"
                                      onClick={() => handleOpenRawPrefillDialog(index)}
                                    >
                                      <div className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100">
                                        <SearchIcon className="h-3 w-3" />
                                      </div>
                                    </div>
                                  </Dialog.Trigger>

                                  {/* Only show reset button when the displayed value is coming from clinician input and user can edit */}
                                  {canEdit && !isBmiField && (
                                    // Show reset button only when the value being displayed is from clinician input
                                    ((clinicianInputs[key] !== undefined && clinicianInputs[key] !== null) ||
                                      (question.clinicianInput !== null && question.clinicianInput !== undefined &&
                                        !(clinicianInputs[key] === undefined || clinicianInputs[key] === null))) && (
                                      <button
                                        onClick={() => handleResetClinicianInput(key)}
                                        className="cursor-pointer ml-1 mt-2 flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100"
                                        title="Reset to patient data"
                                        aria-label="Reset to patient data"
                                      >
                                        <RefreshIcon className="h-3 w-3" />
                                        <span className="text-xs">Reset</span>
                                      </button>
                                    )
                                  )}

                                  <Dialog.Portal>
                                    <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
                                    <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-[700px] max-h-[65vh] overflow-y-auto">
                                      <div className="flex justify-between items-center mb-4">
                                        <Dialog.Title className="text-base font-semibold text-gray-900">
                                          Patient Form Responses
                                        </Dialog.Title>
                                        <Dialog.Close asChild>
                                          <button className="p-1 rounded-full hover:bg-gray-100">
                                            <XIcon className="h-5 w-5 text-gray-500" />
                                          </button>
                                        </Dialog.Close>
                                      </div>

                                      <div className="mb-4">
                                        <Dialog.Description className="text-xs text-gray-600 mb-1">
                                          Data sources
                                        </Dialog.Description>
                                        <h3 className="font-semibold text-sm mb-2">{question.title}</h3>

                                        {/* Last updated timestamp */}
                                        {(question.clinicianInputUpdatedAt || question.patientPrefillLastUpdatedAt) && (
                                          <div className="text-xs text-gray-500 mb-3 flex items-center">
                                            <ClockIcon className="h-3 w-3 mr-1" />
                                            Last updated: {formatDateTime(question.clinicianInputUpdatedAt || question.patientPrefillLastUpdatedAt)}
                                            {question.clinicianInputUpdatedByName && question.clinicianInput && (
                                              <span className="ml-1">by {question.clinicianInputUpdatedByName}</span>
                                            )}
                                          </div>
                                        )}

                                        <div className="space-y-2 mt-2">
                                          {rawPrefillData.length > 0 ? (
                                            rawPrefillData.map((item, i) => (
                                              <div key={i} className={`border rounded-md p-3 ${item.hasAnswer
                                                ? 'bg-violet-50 shadow-sm border-violet-200'
                                                : 'bg-gray-50 border-dashed border-gray-200 opacity-75'
                                                }`}>
                                                <div className="text-xs text-gray-500 mb-0.5">From: {item.formName}</div>
                                                
                                                {/* Display question title if available */}
                                                {item.questionTitle && (
                                                  <div className="text-xs font-medium text-gray-700 mb-1">{item.questionTitle}</div>
                                                )}
                                                
                                                <div className="text-xs font-medium mb-1.5">{item.question}</div>

                                                {/* Display skip badge if question was skipped */}
                                                {item.wasSkipped && (
                                                  <div className="text-xs text-gray-500 flex justify-start items-center mb-2">
                                                    <span>Patient clicked</span>
                                                    <span className="text-xs bg-violet-50 shadow-sm border-violet-200 border text-gray-800 font-semibold px-2 py-1 rounded-md ml-2 whitespace-nowrap">
                                                      {item.skipButtonText}
                                                    </span>
                                                  </div>
                                                )}

                                                {/* Display the answer — FormattedAnswer renders any otherValue
                                                    free text inline in italic curly quotes. */}
                                                <div className={`p-2 rounded border whitespace-pre-wrap text-xs ${item.hasAnswer
                                                  ? 'bg-white border-gray-200'
                                                  : 'bg-gray-100 border-gray-200 border-dashed text-gray-500 italic'
                                                  }`}>
                                                  <FormattedAnswer
                                                    rawAnswer={item.rawAnswer}
                                                    displayText={rawPrefillData.length === 1 && dialogPrefillText ? dialogPrefillText : item.answer}
                                                  />
                                                </div>
                                              </div>
                                            ))
                                          ) : (
                                            <p className="text-gray-500 italic">No source data available</p>
                                          )}
                                        </div>
                                      </div>
                                    </Dialog.Content>
                                  </Dialog.Portal>
                                </Dialog.Root>
                              )}
                            </div>
                          </div>
                        );
                      })
                    }
                  </div>



                  {/* Questions for this subsection */}
                  <div className="space-y-4">
                    {Object.entries(subsection.questions || {}).map(([questionId, question]) => {
                      if (!question) return null;

                      // Check if this question should be shown based on conditions
                      const shouldShow = checkConditionsToShow(question.conditionsToShow);

                      return (
                        <React.Fragment key={questionId}>
                          {shouldShow && (
                            <div className="mb-4 flex items-start">
                              <div className="w-96 flex items-center gap-2">
                                {question.tooltipText ? (
                                  <>
                                    <QuestionMarkCircleIcon
                                      className="w-4 h-4 text-violet-700 cursor-help"
                                      data-tooltip-id={`${question.firestoreFieldName}-tooltip`}
                                      data-tooltip-content={question.tooltipText}
                                    />
                                    <Tooltip
                                      id={`${question.firestoreFieldName}-tooltip`}
                                      place="top"
                                      style={{
                                        maxWidth: '300px',
                                        whiteSpace: 'normal',
                                        backgroundColor: 'rgba(34, 34, 34, 1)',
                                        borderRadius: '15px',
                                        color: '#fff',
                                        fontSize: '0.8em'
                                      }}
                                    />
                                  </>
                                ) : (
                                  <div className="w-4 h-4"></div>
                                )}
                                <h4 className="text-left font-semibold text-gray-700">
                                  {question.questionTitle}
                                </h4>
                              </div>

                              <div className="ml-5">
                                {subsection.subsectionType === 'verticalOptions' && (
                                  <div className="w-96 mt-2">
                                    <SelectionOptions
                                      question={question}
                                      formState={formState}
                                      handleSelect={handleSelect}
                                      handleOtherTextChange={handleOtherTextChange}
                                      handleOtherTextBlur={handleOtherTextBlur}
                                      canEdit={canEdit}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            }

            // Regular subsection rendering for non-Physical Health Review subsections
            return (
              <div key={subsectionId} className="p-8 mb-8 bg-gray-50 rounded-2xl border border-gray-200">
                <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                  {computedTitle}
                </h3>

                <div className="space-y-4">
                  {Object.entries(subsection.questions || {}).map(([questionId, question]) => {
                    if (!question) return null;

                    // Check if this question should be shown based on conditions
                    const shouldShow = checkConditionsToShow(question.conditionsToShow);

                    return (
                      <React.Fragment key={questionId}>
                        {shouldShow && (
                          <div className="mb-4 flex items-start">
                            <div className="w-96 flex items-center gap-2">
                              {question.tooltipText ? (
                                <>
                                  <QuestionMarkCircleIcon
                                    className="w-4 h-4 text-violet-700 cursor-help"
                                    data-tooltip-id={`${question.firestoreFieldName}-tooltip`}
                                    data-tooltip-content={question.tooltipText}
                                  />
                                  <Tooltip
                                    id={`${question.firestoreFieldName}-tooltip`}
                                    place="top"
                                    style={{
                                      maxWidth: '300px',
                                      whiteSpace: 'normal',
                                      backgroundColor: 'rgba(34, 34, 34, 1)',
                                      borderRadius: '15px',
                                      color: '#fff',
                                      fontSize: '0.8em'
                                    }}
                                  />
                                </>
                              ) : (
                                <div className="w-4 h-4"></div>
                              )}
                              <h4 className="text-left font-semibold text-gray-700">
                                {question.questionTitle}
                              </h4>
                            </div>

                            <div className="ml-5">
                              {subsection.subsectionType === 'verticalOptions' && (
                                <div className="w-96 mt-2">
                                  <SelectionOptions
                                    question={getDisplayQuestion(question)}
                                    formState={formState}
                                    handleSelect={handleSelect}
                                    handleOtherTextChange={handleOtherTextChange}
                                    handleOtherTextBlur={handleOtherTextBlur}
                                    canEdit={canEdit}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Navigation Buttons */}
          <div className="flex justify-end items-center mt-8 pt-4 border-t border-gray-200">
            {/* Conditionally render either Next Section button or Finish button */}
            {isBeginningMedicationNow ? (
              <button
                onClick={handleNextSection}
                disabled={(canEdit && !formValid) || isNavigating}
                className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto ${(canEdit && !formValid) || isNavigating
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'hover:bg-violet-800 bg-violet-900 text-white'
                  }`}
              >
                <span className="font-bold">{isNavigating ? 'Loading...' : `Go to ${nextSectionTitle}`}</span>
                {isNavigating
                  ? <ButtonSpinner className="w-5 h-5 ml-2" />
                  : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
              </button>
            ) : (
              canEdit && (
                <button
                  onClick={handleFinish}
                  disabled={!formValid || isSaving || isNavigating}
                  className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto ${!formValid
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : (isSaving || isNavigating)
                        ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white'
                        : 'hover:bg-violet-800 bg-violet-900 text-white'
                    }`}
                >
                  <span className="font-bold">{isSaving ? "Saving Progress..." : "Finish Post-Assessment Discussion"}</span>
                  {isSaving ? <ButtonSpinner className="w-5 h-5 ml-2" /> : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                </button>
              )
            )}
          </div>

        </div>

        <ModuleCompletionDialog
          open={isDialogOpen && !isGenerating}
          onOpenChange={setIsDialogOpen}
          title="Post-Assessment Discussion Complete"
          width={600}
          actions={
            <>
              <CancelButton onClick={() => setIsDialogOpen(false)} />
              <GenerateReportButton onClick={handleGenerateReport} />
            </>
          }
        >
          <SummarySection title="Summary:">
            The Post-Assessment Discussion has been completed. Would you like to generate the report?
          </SummarySection>
        </ModuleCompletionDialog>

        <GenerateReportProgressDialog
          reportStage="Post-Assessment Discussion"
          patientId={patientId}
          clinicianId={auth.currentUser?.uid || ''}
          handleNextPageButtonClick={handleNextPageButtonClick}
          isGenerating={isGenerating}
          onCancel={handleCancelGeneration}
        />
      </div>
    </Layout>
  );
}